import { isEmpty, isNil } from 'lodash';

import { getTypedLanguage } from './i18nutils';
import { CustomField, CustomFieldType, CustomLabel, Language, UpdateCustomFieldInput } from './types';

export const generateDatabaseFieldName = (labels: CustomLabel[]) => {
  const englishLabel = labels.find((label) => label.language === Language.EN)?.label ?? labels[0].label;
  return englishLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

export const prepareDefaultValueForDatabase = (type: CustomFieldType, value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (type === CustomFieldType.CHECKBOX) {
    if (Array.isArray(value) && isEmpty(value)) {
      return null;
    }
    return JSON.stringify(value);
  }
  return value.toString();
};

export const getTranslatedTitle = (labels: CustomLabel[], currentLanguage: Language, fallback: string) => {
  if (isEmpty(labels)) {
    return fallback;
  }

  if (labels[0].label === '') {
    return fallback;
  }

  const currentLangLabel = labels.find((label) => getTypedLanguage(label.language) === currentLanguage);
  if (currentLangLabel?.label) {
    return currentLangLabel.label;
  }

  const englishLabel = labels.find((label) => getTypedLanguage(label.language) === Language.EN);
  if (englishLabel?.label) {
    return englishLabel.label;
  }
  return labels[0].label ?? fallback;
};

export const sortCustomFieldsByOrder = (fields: CustomField[]): CustomField[] => {
  if (fields.every((field) => isNil(field.order))) {
    return fields;
  }
  return [...fields].sort((a, b) => {
    if (isNil(a.order) && isNil(b.order)) {
      return 0;
    }
    if (isNil(a.order)) {
      return 1;
    }
    if (isNil(b.order)) {
      return -1;
    }
    return a.order - b.order;
  });
};

export const buildReorderedCustomFieldsPayload = (orderedFields: CustomField[]): UpdateCustomFieldInput[] =>
  orderedFields.reduce<UpdateCustomFieldInput[]>((accumulator, field, index) => {
    if (field.order !== index) {
      accumulator.push({ id: field.id, order: index, _version: field._version });
    }
    return accumulator;
  }, []);
